import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { VideoLoader } from './engine/loader';
import { chooseTier, type Tier } from './engine/tiers';
import {
  applyReducedMotion,
  prefersReducedMotion,
  watchReducedMotion,
} from './engine/a11y';
import { mountHero } from './scenes/hero';
import { mountChapter } from './scenes/chapter';
import {
  VOICE_BRIDGE,
  COMMAND,
  EVERYDAY_APPS,
  QUANT_SQUADRON,
  CREATION_BAY,
} from './content';
import { mountOutro } from './scenes/outro';
import { startRum, recordTier, recordSeekP95 } from './rum';
import './style.css';

gsap.registerPlugin(ScrollTrigger);

const USER_TIER_KEY = 'tamze-fleet-tier';

/**
 * Tier selection is fully automatic (computer → desktop scrub, phone →
 * mobile/portrait). The only user-facing choice is motion on/off (static
 * posters). A `?tier=desktop|mobile` URL param remains as a debug override.
 */
function readStaticPref(): boolean {
  try {
    const v = localStorage.getItem(USER_TIER_KEY);
    if (v === 'static') return true;
    // Migrate away stale tier overrides from the old dropdown.
    if (v !== null) localStorage.removeItem(USER_TIER_KEY);
  } catch {
    /* private mode */
  }
  return false;
}

function writeStaticPref(on: boolean): void {
  try {
    if (on) localStorage.setItem(USER_TIER_KEY, 'static');
    else localStorage.removeItem(USER_TIER_KEY);
  } catch {
    /* ignore */
  }
}

function readDebugTier(): Tier | null {
  const t = new URLSearchParams(window.location.search).get('tier');
  return t === 'desktop' || t === 'mobile' ? t : null;
}

function setupMotionToggle(staticMode: boolean): void {
  const btn = document.querySelector<HTMLButtonElement>('#motion-toggle');
  if (!btn) return;
  btn.textContent = staticMode ? 'Motion: off' : 'Motion: on';
  btn.setAttribute('aria-pressed', String(staticMode));
  btn.addEventListener('click', () => {
    writeStaticPref(!staticMode);
    // Full remount of media is Phase 1 territory; reload for correct assets
    window.location.reload();
  });
}

function boot(): void {
  const root = document.getElementById('app');
  if (!root) throw new Error('#app missing');

  startRum();

  const staticMode = readStaticPref();
  const reduced = prefersReducedMotion() || staticMode;
  const tier = chooseTier({ userOverride: readDebugTier() });
  recordTier(tier);

  // Desktop gets 3 decoded slots (1080p tiers are light): active + 2 prefetched.
  // Constrained tiers keep the original ≤2 cap.
  const loader = new VideoLoader({
    concurrency: 2,
    retries: 2,
    maxAlive: tier === 'desktop' ? 3 : 2,
  });

  if (reduced) {
    const videos = [
      ...root.querySelectorAll<HTMLVideoElement>('.scene-video'),
    ];
    const posters = [
      ...root.querySelectorAll<HTMLElement>('.scene-poster'),
    ];
    applyReducedMotion({ root: document.documentElement, videos, posters });
  }

  const ctx = { tier, reducedMotion: reduced, staticMode, loader, root };

  const hero = mountHero(ctx);
  const voice = mountChapter(VOICE_BRIDGE, ctx);
  const command = mountChapter(COMMAND, ctx);
  const everyday = mountChapter(EVERYDAY_APPS, ctx);
  // Quant's dive covers more camera distance per frame than the other shots,
  // so it reads "shorter" at equal travel — extra runway evens the pacing.
  const quant = mountChapter(QUANT_SQUADRON, ctx, { pinEnd: '+=210%' });
  const creation = mountChapter(CREATION_BAY, ctx);
  const outro = mountOutro(ctx);

  // Warm every scene SEQUENTIALLY in page order starting at load — parallel
  // prefetch splits bandwidth so nothing finishes before the user arrives.
  // (Hero warms itself first inside mountHero; it is tiny.)
  if (!reduced) {
    [voice, command, everyday, quant, creation, outro].reduce(
      (prev, handle) =>
        prev.then(() => handle.warmup?.() ?? Promise.resolve()),
      hero.warmup?.() ?? Promise.resolve(),
    );
  }

  setupMotionToggle(staticMode);

  // Periodic seek p95 report (first loads excluded inside Scrubber)
  const reportSeeks = (): void => {
    const samples = [
      ...(hero.scrubber?.seekSamplesMs ?? []),
      ...(voice.scrubber?.seekSamplesMs ?? []),
      ...(command.scrubber?.seekSamplesMs ?? []),
      ...(everyday.scrubber?.seekSamplesMs ?? []),
      ...(quant.scrubber?.seekSamplesMs ?? []),
      ...(creation.scrubber?.seekSamplesMs ?? []),
      ...(outro.scrubber?.seekSamplesMs ?? []),
    ];
    if (!samples.length) return;
    const sorted = [...samples].sort((a, b) => a - b);
    const idx = Math.min(
      sorted.length - 1,
      Math.max(0, Math.ceil(0.95 * sorted.length) - 1),
    );
    recordSeekP95(sorted[idx] ?? null);
  };
  const seekTimer = window.setInterval(reportSeeks, 5000);

  watchReducedMotion((rm) => {
    if (rm) window.location.reload();
  });

  // Debounced resize remaps pin distances (Phase 0 minimal)
  let resizeTimer = 0;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      ScrollTrigger.refresh();
    }, 150);
  });

  window.addEventListener(
    'pagehide',
    () => {
      window.clearInterval(seekTimer);
      hero.dispose();
      voice.dispose();
      command.dispose();
      everyday.dispose();
      quant.dispose();
      creation.dispose();
      outro.dispose();
      loader.disposeAll();
    },
    { once: true },
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
